import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import { benefitRows, diningNotes, diningOffer, overviewIntroduction, PAGE_TITLE, tiers } from './benefits';
import './style.scss';

function DiningNotes({ onDetails, extended = false }) {
  return (
    <div className={`langham-notes${extended ? ' langham-notes-extended' : ''}`}>
      <p>{diningNotes[0]}<button type="button" className="langham-text-link" onClick={onDetails} data-dining-details aria-label="查看香港餐饮优惠详情">按此</button></p>
      {diningNotes.slice(1).map((text) => <p key={text}>{text}</p>)}
      {extended && <div className="langham-conditions"><p>#视供应情况而定</p><p>^不包括度假酒店</p></div>}
    </div>
  );
}

function BenefitsOverview({ onDiningDetails }) {
  return (
    <>
      <p className="langham-overview-intro">{overviewIntroduction}</p>
      <table className="langham-comparison" aria-label="朗廷卓逸会礼遇对照表">
        <thead className="langham-sr-only">
          <tr><th scope="col">礼遇</th>{tiers.map((tier) => <th scope="col" key={tier.id}>{tier.name}</th>)}</tr>
        </thead>
        <tbody>
          {benefitRows.map((benefit) => (
            <tr key={benefit.name}>
              <th scope="row">{benefit.emphasized ? <strong>{benefit.name}</strong> : benefit.name}</th>
              {benefit.values.map((value, index) => (
                <td key={tiers[index].id}>
                  <span className="langham-level-name" aria-hidden="true">{tiers[index].name}</span>
                  {value === true ? (
                    <span className={`langham-gem langham-gem-${tiers[index].id}`}><span className="langham-sr-only">享有</span></span>
                  ) : value ? <span>{value}</span> : <span className="langham-sr-only">未列示</span>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <DiningNotes extended onDetails={onDiningDetails} />
    </>
  );
}

function DiningDetails() {
  return (
    <article className="langham-dining">
      <img src={diningOffer.image} alt="Brilliant Feasts 餐饮礼遇" width="900" height="600" />
      <h3>{diningOffer.title}</h3>
      <p className="langham-dining-location">{diningOffer.location}</p>
      {diningOffer.introduction.map((text) => <p key={text}>{text}</p>)}
      <section>
        <h3>{diningOffer.heading}</h3>
        <p>{diningOffer.description}</p>
      </section>
      {diningOffer.restaurants.map((restaurant) => (
        <section key={restaurant.name}>
          <h3>{restaurant.name}</h3>
          <p>{restaurant.description}</p>
        </section>
      ))}
      <section className="langham-venues">
        <h3>参与餐厅 (仅限香港地区) :</h3>
        {diningOffer.venues.map((group, index) => (
          <div key={group.hotel || index}>
            {group.hotel && <h4>{group.hotel}</h4>}
            {group.names.map((name) => <p key={name}>{name}</p>)}
          </div>
        ))}
      </section>
      <section className="langham-terms">
        <h3>条款及细则</h3>
        <ol>{diningOffer.terms.map((term) => <li key={term}>{term}</li>)}</ol>
      </section>
    </article>
  );
}

export default function BrilliantByLanghamBenefits() {
  const [activeTier, setActiveTier] = useState(0);
  const [view, setView] = useState(null);
  const [returnToOverview, setReturnToOverview] = useState(false);
  const pageRef = useRef(null);
  const tabsRef = useRef(null);
  const dialogScrollRef = useRef(null);
  const overviewScrollRef = useRef(0);
  const isOpen = Boolean(view);

  useEffect(() => {
    if (!isOpen) return undefined;
    // The H5 shell scrolls #container rather than the document body.
    const container = pageRef.current.closest('#container');
    if (!container) return undefined;
    const overflow = container.style.overflowY;
    container.style.overflowY = 'hidden';
    return () => { container.style.overflowY = overflow; };
  }, [isOpen]);

  useLayoutEffect(() => {
    const scroll = dialogScrollRef.current;
    if (!scroll) return;
    if (view === 'overview' && returnToOverview) {
      scroll.querySelector('[data-dining-details]')?.focus({ preventScroll: true });
    }
    scroll.scrollTop = view === 'overview' ? overviewScrollRef.current : 0;
  }, [view, returnToOverview]);

  function selectTier(index, focus = false) {
    setActiveTier(index);
    if (focus) tabsRef.current.querySelectorAll('[role="tab"]')[index].focus();
  }

  function handleTabKey(event) {
    let next;
    if (event.key === 'ArrowRight') next = (activeTier + 1) % tiers.length;
    if (event.key === 'ArrowLeft') next = (activeTier + tiers.length - 1) % tiers.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = tiers.length - 1;
    if (next !== undefined) {
      event.preventDefault();
      selectTier(next, true);
    }
  }

  function openOverview() {
    overviewScrollRef.current = 0;
    setReturnToOverview(false);
    setView('overview');
  }

  function openDining() {
    setReturnToOverview(view === 'overview');
    if (view === 'overview') overviewScrollRef.current = dialogScrollRef.current.scrollTop;
    setView('dining');
  }

  return (
    <main className="langham-benefits" ref={pageRef}>
      <section aria-label="会籍级别">
        <h1 aria-label={PAGE_TITLE}><span>朗廷卓逸会</span><span>会员礼遇</span></h1>
        <div className="langham-tabs" role="tablist" aria-label="会籍级别" ref={tabsRef} onKeyDown={handleTabKey}>
          {tiers.map((tier, index) => (
            <button
              key={tier.id}
              type="button"
              id={`langham-tab-${tier.id}`}
              role="tab"
              aria-controls={`langham-panel-${tier.id}`}
              aria-selected={activeTier === index}
              tabIndex={activeTier === index ? 0 : -1}
              onClick={() => selectTier(index)}
            >{tier.name}</button>
          ))}
        </div>
        {tiers.map((tier, index) => (
          <div className="langham-tier-panel" key={tier.id} id={`langham-panel-${tier.id}`} role="tabpanel" aria-labelledby={`langham-tab-${tier.id}`} hidden={activeTier !== index}>
            {activeTier === index && (
              <>
                <div className="langham-tier-card">
                  <img src={tier.image} alt={`${tier.name}会籍卡`} width="700" height="437" />
                  <p>{tier.points}</p>
                </div>
                <div className="langham-tier-summary">
                  <h2>{tier.name}</h2>
                  <p>{tier.introduction}</p>
                  <ul className={tier.id === 'onyx' ? 'langham-onyx-list' : undefined}>
                    {tier.benefits.map((benefit) => <li key={benefit}>{benefit}</li>)}
                  </ul>
                </div>
              </>
            )}
          </div>
        ))}
        <DiningNotes onDetails={openDining} />
        <button className="langham-overview-button" type="button" onClick={openOverview} aria-haspopup="dialog">礼遇概览</button>
      </section>
      <Modal
        open={isOpen}
        onCancel={() => setView(null)}
        footer={null}
        centered
        width={420}
        rootClassName="langham-modal"
        title={
          <>
            {view === 'dining' && returnToOverview && (
              <button type="button" className="langham-back" title="返回礼遇概览" aria-label="返回礼遇概览" onClick={() => setView('overview')}><LeftOutlined /></button>
            )}
            <span>{view === 'dining' ? '餐饮优惠详情' : '礼遇概览'}</span>
          </>
        }
      >
        {view && (
          <div className="langham-modal-scroll" ref={dialogScrollRef} role="region" aria-label={view === 'overview' ? '礼遇概览内容' : '餐饮优惠详情内容'} tabIndex={0}>
            {view === 'overview' ? <BenefitsOverview onDiningDetails={openDining} /> : <DiningDetails />}
          </div>
        )}
      </Modal>
    </main>
  );
}
